/**
 * Microsoft Clarity (project y1j2fivex4, "SWTPA Web") — free unlimited session
 * replay + heatmaps, so we can WATCH what real ad-click visitors do instead of
 * inferring it from funnel counts.
 *
 * Same gating discipline as the ad pixels (metaPixel/redditPixel):
 *   - prod builds only
 *   - never during the build-time prerender (its injected <script> would be
 *     serialized into the shipped static HTML — the 2026-08-12 pixel bug)
 *   - never for internal traffic (?internal=1 flag) — recordings must be real
 *     visitors, not founder/QA walks
 *   - never on the STAGING host (#411). The staging build is a PROD build
 *     (`npm run build:staging` → import.meta.env.PROD is true), so without this
 *     check every persona walk and every screenshot e2e run would land in the
 *     same replay project as real visitors and quietly poison it.
 *
 * Nothing loads until the visitor accepts the replay consent bar
 * (ReplayConsentBar); a decline or no answer means Clarity never loads.
 *
 * Clarity's default "Balanced" masking hides typed input text (email capture
 * stays private). CSP: www.clarity.ms is in script-src, *.clarity.ms in
 * connect-src (vercel.json).
 */
import { isInternalTraffic } from './internalTraffic';
import { isPrerendering } from './prerender';

const CLARITY_PROJECT_ID = 'y1j2fivex4';

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

let initialized = false;

const CONSENT_KEY = 'swtpa_replay_consent';
export type ReplayConsent = 'accepted' | 'declined';

export function getReplayConsent(): ReplayConsent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'accepted' || v === 'declined' ? v : null;
  } catch {
    return null;
  }
}

export function setReplayConsent(choice: ReplayConsent): void {
  try {
    localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    // Storage blocked: fail closed, Clarity stays off.
  }
  if (choice === 'accepted') initClarity();
}

/**
 * The i9 staging host, published over the tailnet behind Caddy. Any `.ts.net`
 * host is a tailnet host and can only be a staging/QA walk, so the whole suffix
 * is gated rather than the one name — a second staging host must not silently
 * start recording.
 */
function isStagingHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'i9.tail458c6d.ts.net' || host.endsWith('.ts.net');
}

function eligible(): boolean {
  return import.meta.env.PROD && !isPrerendering() && !isInternalTraffic() && !isStagingHost();
}

/** True when Clarity would record this visitor but they have not answered yet. */
export function shouldAskReplayConsent(): boolean {
  return eligible() && getReplayConsent() === null;
}

/** Call once at app boot (after internal-traffic flag handling). */
export function initClarity(): void {
  if (initialized || !eligible() || getReplayConsent() !== 'accepted') return;
  initialized = true;
  const w = window as unknown as { clarity?: ClarityFn };
  if (!w.clarity) {
    const queued: ClarityFn = ((...args: unknown[]) => {
      queued.q = queued.q || [];
      queued.q.push(args);
    }) as ClarityFn;
    w.clarity = queued;
  }
  const s = document.createElement('script');
  s.src = 'https://www.clarity.ms/tag/' + CLARITY_PROJECT_ID;
  s.async = true;
  document.head.appendChild(s);
}
